import React from "react";

/**
 * Extracts sustainable feature labels from global properties
 * @returns {Object} Object containing all sustainable feature labels
 */
export const getSustainableLabels = () => {
  const {
    mlSourceReduced = "",
    mlCommerciallyCompostable = "",
    mlRecycledContentLabel = "",
    mlRenewableResourcesLabel = "",
    productMadeInTheUSALabel = "",
    recyclableLabel = "",
    sfiCertifiedLabel = "",
    fscCertifiedLabel = "",
    proPlanetSustainableLabel = "",
    designedForRecyclingLabel = "",
    topRackDishwasherSafeLabel = "",
    aprLabel = "",
  } = window.globalProperties || {};

  return {
    mlSourceReduced,
    mlCommerciallyCompostable,
    mlRecycledContentLabel,
    mlRenewableResourcesLabel,
    productMadeInTheUSALabel,
    recyclableLabel,
    sfiCertifiedLabel,
    fscCertifiedLabel,
    proPlanetSustainableLabel,
    designedForRecyclingLabel,
    topRackDishwasherSafeLabel,
    aprLabel,
  };
};

/**
 * Maps product properties to their corresponding sustainable feature labels
 * @param {Object} product - The product object with boolean properties
 * @param {Object} labels - The labels object from getSustainableLabels()
 * @returns {Array} Array of objects with label and isProPlanet flag
 */
export const getSustainableFeaturesList = (product, labels) => {
  const {
    mlSourceReduced,
    mlCommerciallyCompostable,
    mlRecycledContentLabel,
    mlRenewableResourcesLabel,
    productMadeInTheUSALabel,
    recyclableLabel,
    sfiCertifiedLabel,
    fscCertifiedLabel,
    proPlanetSustainableLabel,
    designedForRecyclingLabel,
    topRackDishwasherSafeLabel,
    aprLabel,
  } = labels;

  const sustainableFeatures = [
    { label: product["isProPlanet"] ? proPlanetSustainableLabel : null, isProPlanet: true },
    { label: product["isDesignedForRecycling"] ? designedForRecyclingLabel : null, isProPlanet: false },
    { label: product["isMadeInUSA"] ? productMadeInTheUSALabel : null, isProPlanet: false },
    { label: product["isRecyclable"] ? recyclableLabel : null, isProPlanet: false },
    { label: product["isRecycledContent"] ? mlRecycledContentLabel : null, isProPlanet: false },
    { label: product["isCompostable"] ? mlCommerciallyCompostable : null, isProPlanet: false },
    { label: product["isRenewableContent"] ? mlRenewableResourcesLabel : null, isProPlanet: false },
    { label: product["isSFICertified"] ? sfiCertifiedLabel : null, isProPlanet: false },
    { label: product["isFSCCertified"] ? fscCertifiedLabel : null, isProPlanet: false },
    { label: product["isSourceReduced"] ? mlSourceReduced : null, isProPlanet: false },
    { label: product["isTopRackDishwasherSafe"] ? topRackDishwasherSafeLabel : null, isProPlanet: false },
    { label: product["isAPR"] ? aprLabel : null, isProPlanet: false },
  ];

  return sustainableFeatures
    .filter((item) => item.label !== null && item.label !== "");
};

/**
 * Renders sustainable features as a list
 * @param {Object} props - Component props
 * @param {Object} props.product - The product object
 * @param {string} props.className - Base class name for styling
 * @param {string} props.checkmarkIconClass - Class for checkmark icon (default: "darticon-checkmark")
 * @param {string} props.proPlanetIconClass - Class for ProPlanet icon (default: "darticon-proPlanet_Leaf")
 * @returns {JSX.Element|null} Rendered list of features or null if no features
 */
export const SustainableFeatures = ({
  product,
  className = "",
  checkmarkIconClass = "darticon-checkmark",
  proPlanetIconClass = "darticon-proPlanet_Leaf",
}) => {
  const labels = getSustainableLabels();
  const features = getSustainableFeaturesList(product, labels);

  if (features.length === 0) {
    return null;
  }

  return (
    <ul className={className}>
      {features.map((feature, index) => {
        const iconClass = feature.isProPlanet && feature.label === labels.proPlanetSustainableLabel
          ? proPlanetIconClass
          : checkmarkIconClass;
        
        return (
          <li key={index}>
            <span className={iconClass}></span>
            {feature.label}
          </li>
        );
      })}
    </ul>
  );
};

export default SustainableFeatures;
